import React, {
	useMemo,
	useRef,
	useContext,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import TitleCard from '../general-components/title-card';
import { TitlesContext, ANIMATION_STATES } from '../../contexts/titles/titles-context';
import useProfile from '../../hooks/use-profile';
import { OnsiteLocation } from '../../stores/reducers/video';

interface OwnProps {
	idOfPresenter?: number | null;
	doAnimateThumb?: boolean; // for the thumbnail of the live shot
	onsiteLocation?: OnsiteLocation;
}

const animationMap = new Map([[Infinity || null, ''], [5, 'five'], [10, 'ten']]);

export default function Title(props: OwnProps) {
	const {
		idOfPresenter,
		doAnimateThumb = false,
		onsiteLocation,
	} = props;

	const hiddenTitles = useSelector((s: GS) => s.VideoReducer.hiddenTitles);
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const useMasks = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_profile_masks);
	const {
		videoTitlesHaveFired,
		canvasStyle: {
			titlesAnimation,
			titlesDuration,
			titlesPosition,
			titlesSelectedCard,
		},
	} = useSelector((state: GS) => state.ShotsReducer);

	const {
		hidden,
		isCanvas,
		animationState,
		onAnimationEnd,
		onAnimationStart,
	} = useContext(TitlesContext);

	const titleRef = useRef<HTMLDivElement | null>(null);

	const userUnflagged = useMemo(
		() => connectedUsers.find((user) => user.gr_user === idOfPresenter && user.in_room),
		[idOfPresenter, connectedUsers],
	);

	const { getMaskedProfileById } = useProfile({});

	const masked = useMemo(() => {
		if (!useMasks) return null;
		if (!idOfPresenter) return null;
		return getMaskedProfileById(idOfPresenter);
	}, [userUnflagged, useMasks, getMaskedProfileById, idOfPresenter]);

	// positioning currently only available for basic title (title 3), not svg titles
	const position = titlesSelectedCard === 3 ? titlesPosition : 'bottom-left';

	const titles = useMemo(() => {
		const user = (masked && useMasks) ? masked : userUnflagged;
		if ((!user && !onsiteLocation) || hidden) return <></>;

		if (!isCanvas && !doAnimateThumb) {
			// a static render with no animation, for most shot thumbs
			return <div
				className={classNames(
					'title-overlay',
					position,
				)}
			>
				<TitleCard
					user={user}
					cardType={titlesSelectedCard}
					preview={false}
					position={position}
					onsiteLocation={onsiteLocation}
				/>
			</div>;
		}

		if (
			hiddenTitles[0] !== 1
			&& animationState !== ANIMATION_STATES.MANUAL_OUT
		) {
			// if the titles have a duration, they will stay in
			// this render until the below element animates out
			return <div
				className={classNames(
					'title-overlay',
					position,
					titlesAnimation,
					animationMap.get(titlesDuration),
					{ hidden: videoTitlesHaveFired },
				)}
				ref={titleRef}
				onAnimationStart={onAnimationStart}
				onAnimationEnd={onAnimationEnd}
			>
				<TitleCard
					user={user}
					cardType={titlesSelectedCard}
					preview={false}
					position={position}
					onsiteLocation={onsiteLocation}
				/>
			</div>;
		}

		if (animationState === ANIMATION_STATES.MANUAL_OUT) {
			// Title is toggled off but hasn't animated out yet.
			// We change the render and re-set the animation to
			// stop any current animation in or out.
			if (titleRef.current) {
				// Added this code to remove the animation css from the original title
				// (https://stackoverflow.com/questions/21664940/force-browser-to-trigger-reflow-while-changing-css)
				// before re-adding the title with the opposite animation to manually remove it.
				// If the original title had a timed animation then there is some weirdness with adding this
				// title to immedatitely animate it out and some leftover animation styling existing from
				// the original title. This clears the original title animation styling which is necessary.
				titleRef.current.style.animation = 'none';
				// eslint-disable-next-line
				titleRef.current.offsetHeight; /* trigger reflow */
				titleRef.current.style.animation = '';
			}

			return (
				<div
					onAnimationEnd={onAnimationEnd}
					className={classNames(
						'title-overlay',
						position,
						{
							'dissolve-out': titlesAnimation === 'dissolve',
							'move-out': titlesAnimation === 'move-in',
							'none-out': titlesAnimation === 'none',
						},
					)}
				>
					<TitleCard
						user={user}
						cardType={titlesSelectedCard}
						preview={false}
						position={position}
						onsiteLocation={onsiteLocation}
					/>
				</div>
			);
		}

		return <></>;
	}, [
		userUnflagged,
		masked,
		useMasks,
		hidden,
		position,
		isCanvas,
		doAnimateThumb,
		animationState,
		titlesDuration,
		titlesAnimation,
		hiddenTitles[0],
		videoTitlesHaveFired,
		titlesSelectedCard,
		onsiteLocation,
	]);

	return (
		<>
			{titles}
		</>
	);
}
